import React, { useState, useEffect, useRef } from 'react';
import { Table, Input } from 'antd';
import toast from 'react-hot-toast';

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  columnIndex,
  columns,
  tableRef,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(record ? record[dataIndex] : '');

  const cellRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (record && dataIndex !== undefined) {
      setValue(record[dataIndex]);
    }
  }, [record, dataIndex]);

  const toggleEdit = () => {
    setEditing(!editing);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };
  
  const save = async () => {
    try {
      const updatedRecord = { ...record, [dataIndex]: value };
      const outValue = Object.keys(updatedRecord)
        .filter((key) => /^score_[1-9]$/.test(key)) 
        .reduce((sum, key) => sum + (parseFloat(updatedRecord[key]) || 0), 0);

      const inValue = Object.keys(updatedRecord)
        .filter((key) => /^score_(1[0-8])$/.test(key)) 
        .reduce((sum, key) => sum + (parseFloat(updatedRecord[key]) || 0), 0);

      handleSave({ ...updatedRecord, out: outValue, in: inValue });
      setEditing(false);
    } catch (errInfo) {
      setEditing(false);
    }
  };

  const handleClickOutside = (event) => {
    if (cellRef.current && !cellRef.current.contains(event.target)) {
      save();
    }
  };

  useEffect(() => {
    if (editing) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      saveAndMoveToNext();
    }
  };

  const saveAndMoveToNext = async () => {
    try {
      await save();
 
      const nextColumnIndex = columnIndex === 9 ? 11 :columnIndex + 1;

      if (nextColumnIndex < columns.length) {
        const currentRowRef = tableRef.current[record.key];
        const nextCell = currentRowRef?.querySelector(
          `td[data-col-index="${nextColumnIndex}"]`
        );

        if (nextCell) {
          const nextInput = nextCell.querySelector('div');
          if (nextInput) {
            nextInput.click();
          } else {
            toggleEdit();
          }
        }
      }
    } catch (err) {
      toast.error('Error moving to next cell:', err);
    }
  };

  const handleInputChange = (e) => {
    setValue(e.target.value);
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <div 
        className='input-editable'
      >
        <Input
          ref={inputRef}
          // value={value}
          onChange={handleInputChange}
          onPressEnter={save}
          onBlur={save}
          onKeyDown={handleKeyDown}
          className='edit-input'
        />
      </div>
    ) : (
      <div 
        className='input-editable'
        // onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return (
    <td
      ref={cellRef}
      {...restProps}
      data-col-index={columnIndex}
      data-row-key={record?.key}
      onClick={toggleEdit}
    >
      {childNode}
    </td>
  );
};

const EditableScoreCard = ({ dataSource, setDataSource, columns, loading}) => {
  const tableRef = useRef({});

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => item.name === row.name);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    setDataSource(newData);
  };

  const components = {
    body: {
      cell: EditableCell,
    },
  };
  
  const columnsConfig = columns.map((col, columnIndex) => {
    if (!col.onCell) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.onCell(record).editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
        columnIndex,
        columns,
        tableRef,
      }),
    };
  });

  return (
    <div>
      <Table
        components={components}
        className="score-table"
        // bordered 
        dataSource={dataSource}
        columns={columnsConfig}
        pagination={false}
        showHeader={false}
        rowClassName={(record, index) => (index===2 ? 'header-table third-row': index < 3 ? 'header-table' :   'edit-table')}
        rowKey="key"
        loading={loading}
        onRow={(record, rowIndex) => {
          return {
            ref: (node) => {
              if (node) {
                tableRef.current[record.key] = node;
              }
            },
          };
        }}
      />
    </div>
  );
};

export default EditableScoreCard;
